import { useEffectOnce } from 'react-use';
import {
  Box,
  type BoxProps,
  Button,
  ButtonGroup,
  Flex,
  Heading,
  IconCalendarTimeOutline,
  IconTrendingOutline,
  IconTrophyOutline,
  List,
  ModalDialog,
  Paragraph,
  Text,
  TextButton,
  Toast,
  Upsell,
  useToast,
} from '@zillow/constellation';
import { PaymentsRequestIcon } from './2PaymentsRequestIcon';
import { useState } from 'react';
import {
  emitPaymentUpsellTipCtaEvent,
  emitPaymentUpsellTipViewEvent,
  emitPaymentUpsellUndoEvent,
} from '../../../../analytics/clickstreamEvents';
import { useApplicationContextOrUndefined } from '../../../../contexts/ApplicationContext';

interface PaymentsUpsellProps {
  onRequestPayments: () => void;
  onUndoRequestPayments: () => void;
}

function PaymentsAndReportsUpsellList() {
  return (
    <List appearance="none">
      <List.Item>
        <Paragraph>
          <IconCalendarTimeOutline marginRight="xs" />
          <Text>Zillow reports on time payments to Experian and Equifax.</Text>
        </Paragraph>
      </List.Item>
      <List.Item>
        <Paragraph>
          <IconTrendingOutline marginRight="xs" />
          <Text>You could build your credit in just a few months.</Text>
        </Paragraph>
      </List.Item>
      <List.Item>
        <Paragraph>
          <IconTrophyOutline marginRight="xs" />
          <Text>Building credit can help you qualify for future loans and more.</Text>
        </Paragraph>
      </List.Item>
    </List>
  );
}

const PAYMENTS_UPSELL_HEADING = 'Build your credit when you pay rent';

export function PaymentsAndReportsUpsellBox({ sx, ...props }: BoxProps) {
  return (
    <Box
      {...props}
      as="div"
      sx={{
        padding: '$spacing.sm',
        backgroundColor: '$colors.backgroundBlue',
        borderRadius: '$radii.sm',
        ...sx,
      }}
    >
      <Heading level={6} as="h2">
        {PAYMENTS_UPSELL_HEADING}
      </Heading>
      <PaymentsAndReportsUpsellList />
      <Paragraph fontColor="textMedium">
        We’ll send a request to your landlord if your application is accepted.
      </Paragraph>
      <Paragraph fontColor="textMedium">Showing interest now won’t affect your application.</Paragraph>
    </Box>
  );
}

export function PaymentsAndReportsTipUpsell({ onRequestPayments, onUndoRequestPayments }: PaymentsUpsellProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const applicationContext = useApplicationContextOrUndefined();

  useEffectOnce(() => {
    emitPaymentUpsellTipViewEvent(applicationContext);
  });

  return (
    <>
      <Upsell
        cardType="secondary"
        background="backgroundBlue"
        direction="column"
        cardElevation={0}
        closeable={false}
        heading={
          <Heading level={4} as="h2" marginBottom="sm">
            {PAYMENTS_UPSELL_HEADING}
          </Heading>
        }
        media={<PaymentsRequestIcon height={75} />}
        body={<PaymentsAndReportsUpsellList />}
        actionButton={
          <TextButton
            type="button"
            onClick={() => {
              setModalOpen(true);
              emitPaymentUpsellTipCtaEvent(applicationContext);
            }}
          >
            Request Zillow payments
          </TextButton>
        }
      />
      <RequestZillowPaymentsModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onRequestPayments={onRequestPayments}
        onUndoRequestPayments={onUndoRequestPayments}
      />
    </>
  );
}

export function RequestZillowPaymentsModal({
  isOpen,
  onClose,
  onRequestPayments,
  onUndoRequestPayments,
}: {
  isOpen: boolean;
  onClose: () => void;
  onRequestPayments: () => void;
  onUndoRequestPayments: () => void;
}) {
  const applicationContext = useApplicationContextOrUndefined();

  const onUndo = () => {
    onUndoRequestPayments();
    emitPaymentUpsellUndoEvent(applicationContext);
    onClose();
  };

  const onOkay = () => {
    onRequestPayments();
    onClose();
  };

  return (
    <ModalDialog
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      renderBody={
        <Flex display="flex" flexDirection="column" gap="sm">
          <Heading level={4} as="h1" fontFamily="serif">
            You’ve requested Zillow payments
          </Heading>
          <Paragraph>We’ll send a request to your landlord if your application is accepted.</Paragraph>
          <Paragraph>Showing your interest now does not affect your application.</Paragraph>
        </Flex>
      }
      renderFooter={
        <ButtonGroup aria-label="Zillow Payment Request actions">
          <TextButton type="button" onClick={onUndo} aria-description="Undo Zillow Payment request">
            Undo
          </TextButton>
          <Button buttonType="primary" type="button" onClick={onOkay} aria-description="Keep Zillow payments request">
            Okay
          </Button>
        </ButtonGroup>
      }
    />
  );
}
interface PaymentRequestToastProps {
  onUndoRequestPayments?: () => void;
}

export function PaymentRequestToast({ onUndoRequestPayments }: PaymentRequestToastProps) {
  const { enqueueToast } = useToast();
  const handleUndoRequestPayments = () => {
    onUndoRequestPayments?.();
    enqueueToast(<Toast appearance="success" body="Request for Zillow payments removed" />);
  };
  return (
    <Toast
      appearance="success"
      body="We’ll send a request to your landlord to report your rent payments to the credit bureaus if your application is accepted."
      actionButton={<TextButton onClick={handleUndoRequestPayments}>Undo</TextButton>}
    />
  );
}
